/** The patient's last web arrival, saved on our api so the desktop QR can carry a short reference. */

import * as http from "./http";

export interface AppLinkVisit {
  /** The tracker's last arrival, JSON-stringified exactly once and never changed afterwards. */
  arrival: string | null;
  uid: string | null;
}

const REF_PATTERN = /^[a-z0-9]{12}$/;

export const getAppLinkVisit = (): AppLinkVisit | null => {
  try {
    const data: any = (window as any).eventsTracking?.getData?.();
    const arrivals: any[] = Array.isArray(data?.arrivals) ? data.arrivals : [];
    const arrival = arrivals.length ? JSON.stringify(arrivals[arrivals.length - 1]) : null;
    const uid = typeof data?.uid === "string" && data.uid ? data.uid : null;
    return { arrival, uid };
  } catch {
    return null;
  }
};

export type AppLinkVisitReason = "saved" | "failed" | "no_customer" | "no_visit";

export interface AppLinkVisitResult {
  ref: string | null;
  reason: AppLinkVisitReason;
}

const CACHE_KEY = "app_link_visit";

// A save on each new day refreshes the row's last-seen date, so the 90-day cleanup never drops a ref AppsFlyer still holds.
const isSavedToday = (savedAt: unknown): boolean =>
  typeof savedAt === "number" && new Date(savedAt).toDateString() === new Date().toDateString();

// The QR waits for the save, so a stalled api must still end in a QR without a ref.
const SAVE_TIMEOUT_MS = 5000;

const pending = new Map<string, Promise<AppLinkVisitResult>>();

// iOS can store only the query string; the save endpoint rejects any url that is not absolute.
const ABSOLUTE_WEB_ADDRESS = /^https?:\/\/[^\s/?#]+/i;
const hasRelativeUrl = (arrival: string): boolean => {
  try {
    const url = JSON.parse(arrival)?.url;
    return typeof url === "string" && !ABSOLUTE_WEB_ADDRESS.test(url);
  } catch {
    return false;
  }
};

export type AppLinkVisitMissing = "arrival" | "relative_arrival" | "uid";

/** Every value the saved visit will lack. */
export const getAppLinkVisitMissing = (visit: AppLinkVisit | null): AppLinkVisitMissing[] => {
  const missing: AppLinkVisitMissing[] = [];
  if (!visit?.arrival) missing.push("arrival");
  else if (hasRelativeUrl(visit.arrival)) missing.push("relative_arrival");
  if (!visit?.uid) missing.push("uid");
  return missing;
};

// The save sends whatever is usable, so one missing value never drops the other.
const toSavableVisit = (visit: AppLinkVisit | null): AppLinkVisit | null => {
  const arrival = visit?.arrival && !hasRelativeUrl(visit.arrival) ? visit.arrival : null;
  const uid = visit?.uid || null;
  return arrival || uid ? { arrival, uid } : null;
};

/** Known without a request, so a banner that saves nothing draws its QR straight away. */
export const getAppLinkVisitSkipReason = (
  customerUuid: string | null | undefined,
  visit: AppLinkVisit | null
): AppLinkVisitReason | null => {
  if (!customerUuid) return "no_customer";
  if (!toSavableVisit(visit)) return "no_visit";
  return null;
};

const readCachedRef = (visit: AppLinkVisit): string | null => {
  try {
    const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null");
    const fresh = isSavedToday(cached?.savedAt);
    const matches = cached?.uid === visit.uid && cached?.arrival === visit.arrival;
    return fresh && matches && typeof cached.ref === "string" && REF_PATTERN.test(cached.ref) ? cached.ref : null;
  } catch {
    return null;
  }
};

const postVisit = async (visit: AppLinkVisit): Promise<AppLinkVisitResult> => {
  try {
    const response = await http.postToServer(
      "url-params-mapping",
      { arrival: visit.arrival, uid: visit.uid },
      [http.MIDDLEWARES.CUSTOMER_ID],
      // The path-only 300 ms dedup can hand a waiting caller nothing.
      { skipDedup: true, timeout: SAVE_TIMEOUT_MS }
    );
    const ref = response?.data?.ref;
    if (typeof ref !== "string" || !REF_PATTERN.test(ref)) return { ref: null, reason: "failed" };
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({ uid: visit.uid, arrival: visit.arrival, ref, savedAt: Date.now() }));
    } catch {
      // Storage blocked: the ref still works for this page.
    }
    return { ref, reason: "saved" };
  } catch {
    return { ref: null, reason: "failed" };
  }
};

/** Never rejects: without a reference the links still work, only the QR loses the visit. */
export const saveAppLinkVisit = (
  customerUuid: string | null | undefined,
  fullVisit: AppLinkVisit | null
): Promise<AppLinkVisitResult> => {
  const skipReason = getAppLinkVisitSkipReason(customerUuid, fullVisit);
  const visit = toSavableVisit(fullVisit);
  if (skipReason || !visit) return Promise.resolve({ ref: null, reason: skipReason ?? "no_visit" });

  const cachedRef = readCachedRef(visit);
  if (cachedRef) return Promise.resolve({ ref: cachedRef, reason: "saved" });

  // Banners on one page share a save, so they send one request and all get its ref.
  const key = JSON.stringify([visit.uid, visit.arrival]);
  const inFlight = pending.get(key);
  if (inFlight) return inFlight;
  const result = postVisit(visit).finally(() => pending.delete(key));
  pending.set(key, result);
  return result;
};
